import { useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { PageHeader } from '@/app/layouts/PageHeader'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { formatMoney, fromMajor, money } from '@/lib/money'
import { cn } from '@/lib/cn'
import { findVenue, WELLNESS_CASHBACK } from './wellness-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import styles from '@/features/transfers/SendAbroadScreen.module.css'

const WELLNESS_BUDGET = 11400

export function WellnessBookingScreen() {
  const navigate = useNavigate()
  const { id = '' } = useParams()
  const [params] = useSearchParams()
  const venue = findVenue(id)

  const requested = params.get('service')
  const [service, setService] = useState(
    () => venue?.services.find((item) => item.name === requested)?.name ?? venue?.services[0]?.name ?? '',
  )
  const [slot, setSlot] = useState(() => venue?.slots[0] ?? '')
  const [fromBudget, setFromBudget] = useState(true)

  const model = useMemo(() => {
    const picked = venue?.services.find((item) => item.name === service)
    const price = fromMajor(picked?.price ?? 0, 'USD')
    const cashback = money(Math.round(price.minorUnits * WELLNESS_CASHBACK), 'USD')
    return {
      picked,
      price,
      cashback,
      overBudget: fromBudget && price.minorUnits > WELLNESS_BUDGET,
      saved: picked?.usPrice ? fromMajor(picked.usPrice - picked.price, 'USD') : null,
    }
  }, [venue, service, fromBudget])

  if (!venue) return <NotFoundScreen />

  const isGym = venue.kind === 'gym'

  return (
    <>
      <PageHeader
        title={isGym ? `Get a pass · ${venue.name}` : `Book ${venue.name}`}
        subtitle={`${venue.city} · ${venue.languages} · free cancellation up to 4 hours before`}
        actions={
          <Button variant="ghost" size="md" iconStart="arrow_back" to={`/wellness/${venue.slug}`}>
            Back to the venue
          </Button>
        }
      />

      <div className={checkout.grid}>
        <Stack gap={3}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              {isGym ? 'Which pass?' : 'What do you need?'}
            </Text>
            <Stack gap={2} className="pb-mt-3">
              {venue.services.map((item) => (
                <button
                  key={item.name}
                  type="button"
                  className={cn(styles.corridor, item.name === service && styles.corridorActive)}
                  onClick={() => setService(item.name)}
                  aria-pressed={item.name === service}
                >
                  <Icon name={item.name === service ? 'radio_button_checked' : 'radio_button_unchecked'} size={20} />
                  <Stack grow gap={0} align="start">
                    <Text variant="caption" weight="semibold">
                      {item.name}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      {item.duration}
                      {item.usPrice ? ` · $${item.usPrice.toLocaleString('en-US')} in the US` : ''}
                    </Text>
                  </Stack>
                  <Text variant="caption" weight="semibold" numeric>
                    {item.price === 0 ? 'Free' : formatMoney(fromMajor(item.price, 'USD'))}
                  </Text>
                </button>
              ))}
            </Stack>
          </Card>

          <Card tone="light">
            <Text variant="caption" weight="semibold">
              {isGym ? 'When do you start?' : 'Pick a time'}
            </Text>
            <Stack direction="row" gap={2} wrap className="pb-mt-3">
              {venue.slots.map((option) => (
                <Button
                  key={option}
                  variant={option === slot ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => setSlot(option)}
                >
                  {option}
                </Button>
              ))}
            </Stack>

            <Text variant="caption" weight="semibold" className="pb-mt-5">
              How do you want to pay?
            </Text>
            <Stack gap={2} className="pb-mt-3">
              {[
                {
                  on: fromBudget,
                  title: 'Wellness budget',
                  body: `$${(WELLNESS_BUDGET / 100).toFixed(2)} left this year`,
                },
                { on: !fromBudget, title: 'US Dollar account', body: '$18,240.50 available' },
              ].map((option) => (
                <button
                  key={option.title}
                  type="button"
                  className={cn(styles.corridor, option.on && styles.corridorActive)}
                  onClick={() => setFromBudget(option.title === 'Wellness budget')}
                  aria-pressed={option.on}
                >
                  <Icon name={option.on ? 'radio_button_checked' : 'radio_button_unchecked'} size={20} />
                  <Stack grow gap={0} align="start">
                    <Text variant="caption" weight="semibold">
                      {option.title}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      {option.body}
                    </Text>
                  </Stack>
                </button>
              ))}
            </Stack>

            {model.overBudget && (
              <Stack direction="row" align="center" gap={2} className="pb-mt-4">
                <Icon name="error" size={16} />
                <Text variant="eyebrow" tone="negative" weight="semibold">
                  That is more than your wellness budget has left. Pay from your USD account instead.
                </Text>
              </Stack>
            )}
          </Card>
        </Stack>

        <Stack gap={3} className={checkout.summary}>
          <Card tone="dark">
            <Card.Header>
              <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">
                {model.picked?.name}
              </Text>
              <Pill tone="accent">5% back</Pill>
            </Card.Header>
            <Text as="p" variant="display" weight="bold" tone="accent" numeric className="pb-mt-3">
              {formatMoney(model.price)}
            </Text>
            {model.saved && (
              <Text variant="caption" tone="onDarkMuted">
                {formatMoney(model.saved)} less than the same treatment in the US
              </Text>
            )}
            <div className={checkout.rule} />
            {[
              ['When', slot],
              ['Where', venue.city],
              ['Paid from', fromBudget ? 'Wellness budget' : 'US Dollar account'],
              ['Booking fee', 'None'],
              ['Cashback', `+${formatMoney(model.cashback)}`],
            ].map(([label, value], index) => (
              <div key={label} className={checkout.line}>
                <Text variant="caption" tone="onDarkMuted">
                  {label}
                </Text>
                <Text
                  variant="caption"
                  weight="semibold"
                  tone={index === 3 || index === 4 ? 'accent' : 'onDark'}
                  numeric
                >
                  {value}
                </Text>
              </div>
            ))}
            <Card.Footer>
              <Button
                size="lg"
                block
                iconStart="lock"
                disabled={model.overBudget}
                onClick={() => navigate(`/wellness/${venue.slug}/done`, { replace: true })}
              >
                {isGym ? 'Get the pass' : 'Confirm the appointment'}
              </Button>
              <Stack direction="row" align="center" gap={2} className="pb-mt-3">
                <Icon name="event_available" size={15} />
                <Text variant="eyebrow" tone="onDarkMuted">
                  Free cancellation up to 4 hours before.
                </Text>
              </Stack>
            </Card.Footer>
          </Card>
        </Stack>
      </div>
    </>
  )
}
